import { Button, Checkbox, FormControlLabel } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { PageContent } from 'templates/PageContent';
import { Form } from 'templates/Form';
import { TransitionButton } from 'atoms/TransitionButton';
import { TermsParser } from 'organisms/u002/TermsParser';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import 'simplebar/dist/simplebar.min.css';

const label = { inputProps: { 'aria-label': 'Consent Checkbox' } };

// 同意画面のテンプレート
export const ConsentContent: React.FC<{
  children?: React.ReactNode;
  title?: string;
  text?: string;
  checked: boolean;
  label?: React.ReactNode;
  onChange?: (checked: boolean) => void;
  nextLabel?: string;
  onNext?: () => void;
  backLabel?: string;
  onBack?: () => void;
}> = (props) => {
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const { t: tCommons } = useTranslationWithLang('commons');
  const termsRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.text && scrollContainerRef.current) {
      const scrollableElement = scrollContainerRef.current.querySelector(
        '.simplebar-content-wrapper'
      );

      if (scrollableElement) {
        const handleScroll = () => {
          const { scrollTop, scrollHeight, clientHeight } = scrollableElement as HTMLElement;
          if (scrollTop + clientHeight >= scrollHeight - 1) {
            setIsScrolledToEnd(true);
          }
        };

        const checkScrollable = () => {
          const { scrollHeight, clientHeight } = scrollableElement as HTMLElement;
          if (scrollHeight <= clientHeight) {
            setIsScrolledToEnd(true);
          }
          handleScroll();
        };

        scrollableElement.addEventListener('scroll', handleScroll);
        checkScrollable();

        const resizeObserver = new ResizeObserver(checkScrollable);
        resizeObserver.observe(scrollableElement);

        return () => {
          scrollableElement.removeEventListener('scroll', handleScroll);
          resizeObserver.disconnect();
        };
      }
    }
  }, [props.text]);
  return (
    <PageContent>
      <h1 className="sr-only" aria-live="assertive">
        {props.title}
      </h1>
      <Form title={props.title ?? ''} aria-labelledby="consent-title">
        <div ref={scrollContainerRef} className="max-h-96" role="region" aria-label={props.title}>
          <SimpleBar style={{ maxHeight: '24rem' }} autoHide={false}>
            <div ref={termsRef} aria-describedby="terms-parser-content" tabIndex={0}>
              <div id="terms-parser-content">
                <TermsParser text={props.text ?? ''} />
              </div>
            </div>
          </SimpleBar>
        </div>

        {!isScrolledToEnd && (
          <div className="sr-only" aria-live="polite">
            {tCommons('TU000_083')}
          </div>
        )}
        <FormControlLabel
          control={
            <Checkbox
              color="secondary"
              {...label}
              checked={props.checked}
              onChange={(e) => props.onChange?.(e.target.checked)}
              disabled={!isScrolledToEnd}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.currentTarget.disabled) {
                  props.onChange?.(!props.checked);
                }
              }}
              inputProps={{
                'aria-label': tCommons('TU000_088'),
                'aria-describedby': 'consent-checkbox-description',
              }}
            />
          }
          label={props.label}
          className="text-[--c-text] flex justify-center items-center"
        />
        <div id="consent-checkbox-description" className="sr-only" aria-live="polite">
          {isScrolledToEnd ? tCommons('TU000_084') : tCommons('TU000_085')}
        </div>

        <div className={'flex justify-center items-center'}>
          {props.onBack && (
            <TransitionButton
              label={props.backLabel}
              onClick={props.onBack}
              className="w-[9rem] mr-2"
              aria-label={props.backLabel}
            />
          )}
          {props.onNext && (
            <Button
              className={'w-[9rem]'}
              variant="contained"
              onClick={props.onNext}
              disabled={!props.checked}
              style={{
                fontWeight: 'bold',
                color: 'var(--c-button-text)',
                borderRadius: '300px',
                padding: '1rem 2rem',
                background: props.checked ? 'var(--c-button-background)' : undefined,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = 'var(--c-button-background-hover)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'var(--c-button-background)')
              }
              aria-label={props.nextLabel}
              aria-disabled={!props.checked}
              aria-describedby="next-button-description"
            >
              {props.nextLabel}
            </Button>
          )}
        </div>
        <div id="next-button-description" className="sr-only">
          {props.checked ? tCommons('TU000_089') : tCommons('TU000_090')}
        </div>
      </Form>
    </PageContent>
  );
};
